import { Goal } from '@/types/goal';
import { format } from 'date-fns';

const SKIP_KEY_PREFIX = 'meq_reflection_skip_';

/**
 * R-03 gate: User needs ≥3 distinct dates with habit completions
 * before the reflection prompt activates.
 */
export function isReflectionActivated(goals: Goal[]): boolean {
  const allDates = new Set<string>();
  for (const goal of goals) {
    for (const habit of goal.habits) {
      for (const date of habit.completedDates) {
        allDates.add(date);
      }
    }
  }
  return allDates.size >= 3;
}

/**
 * R-04: Calculate consecutive reflection days ending at today or yesterday.
 * Dates must be sorted desc (most recent first) — caller provides this.
 */
export function calculateReflectionStreak(dates: string[]): number {
  if (dates.length === 0) return 0;

  const today = format(new Date(), 'yyyy-MM-dd');
  const yesterday = format(
    new Date(Date.now() - 86400000),
    'yyyy-MM-dd'
  );

  // Streak must start from today or yesterday
  const first = dates[0];
  if (first !== today && first !== yesterday) return 0;

  let streak = 1;
  for (let i = 1; i < dates.length; i++) {
    const prev = new Date(dates[i - 1] + 'T00:00:00');
    const curr = new Date(dates[i] + 'T00:00:00');
    const diffDays = (prev.getTime() - curr.getTime()) / 86400000;
    if (diffDays === 1) {
      streak++;
    } else {
      break;
    }
  }

  return streak;
}

/**
 * R-02: Show reflection prompt when all habits are done
 * OR the hour is ≥ 20 (8 PM fallback).
 */
export function shouldShowReflectionPrompt(
  allComplete: boolean,
  totalHabits: number,
  now: Date = new Date()
): boolean {
  if (totalHabits === 0) return false;
  if (allComplete) return true;
  return now.getHours() >= 20;
}

/**
 * Check if the user skipped reflection for a given date.
 */
export function isSkippedToday(dateStr: string): boolean {
  try {
    return localStorage.getItem(SKIP_KEY_PREFIX + dateStr) === '1';
  } catch {
    return false;
  }
}

/**
 * Mark reflection as skipped for the given date.
 */
export function markSkippedToday(dateStr: string): void {
  try {
    localStorage.setItem(SKIP_KEY_PREFIX + dateStr, '1');
  } catch {
    // localStorage unavailable (incognito, etc.)
  }
}
